import React, { useEffect, useState } from "react";
import {
  ArrowDownToLine,
  CheckCircle2,
  Loader2,
  Wallet,
  AlertCircle,
  Sparkles,
  ShieldCheck,
  Network,
  Send,
  ChevronRight,
} from "lucide-react";
import Layout from "../components/Layout";
import { apiRequest } from "../api";

const networks = [
  { value: "TRC20", label: "USDT TRC20" },
  { value: "ERC20", label: "USDT ERC20" },
  { value: "BEP20", label: "USDT BEP20" },
  { value: "Polygon", label: "USDT Polygon" },
];

export default function Withdraw() {
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState(0);
  const [walletAddress, setWalletAddress] = useState("");
  const [network, setNetwork] = useState("");
  const [exchangeName, setExchangeName] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadBalance = async function () {
    try {
      const data = await apiRequest("/users/me");
      const currentBalance = Number(data.usdt_balance || 0);

      setBalance(currentBalance);

      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.usdt_balance = currentBalance;
        localStorage.setItem("user", JSON.stringify(user));
      }
    } catch (err) {
      console.error("LOAD BALANCE ERROR:", err);
    }
  };

  useEffect(function () {
    loadBalance();
  }, []);

  const handleSubmit = async function (e) {
    e.preventDefault();

    setError("");
    setMessage("");

    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError("Please enter a valid withdrawal amount.");
      return;
    }

    if (numericAmount > balance) {
      setError("Insufficient USDT balance.");
      return;
    }

    if (!walletAddress.trim()) {
      setError("Please enter your wallet address.");
      return;
    }

    if (!network) {
      setError("Please select a network.");
      return;
    }

    try {
      setLoading(true);

      const response = await apiRequest("/withdrawals", {
        method: "POST",
        body: JSON.stringify({
          amount: numericAmount,
          wallet_address: walletAddress.trim(),
          network: network,
          exchange_name: exchangeName.trim(),
        }),
      });

      setMessage(
        response.message || "Withdrawal request submitted successfully."
      );

      setAmount("");
      setWalletAddress("");
      setNetwork("");
      setExchangeName("");

      await loadBalance();
    } catch (err) {
      console.error("WITHDRAW ERROR:", err);
      setError(err.message || "Failed to submit withdrawal request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Withdraw USDT">
      <div className="relative mx-auto max-w-3xl space-y-5 pb-8">
        {/* Ambient background */}
        <div className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-96 overflow-hidden">
          <div className="absolute -left-20 -top-32 h-80 w-80 rounded-full bg-[#D946A8]/20 blur-[100px]" />
          <div className="absolute right-0 -top-24 h-80 w-80 rounded-full bg-[#8B5CF6]/15 blur-[110px]" />
          <div className="absolute left-1/2 top-32 h-64 w-64 -translate-x-1/2 rounded-full bg-[#F5C563]/10 blur-[100px]" />
        </div>

        {/* Hero */}
        <div className="group relative overflow-hidden rounded-[28px] border border-[#D946A8]/25 bg-gradient-to-br from-[#32113F] via-[#141320] to-[#0C0E18] p-5 shadow-[0_20px_60px_-30px_rgba(217,70,168,0.45)] sm:p-6">
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#F5C563]/10 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-[#D946A8]/10 blur-[90px]" />

          <div className="relative">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#D946A8] via-[#B355C9] to-[#8B5CF6] shadow-[0_0_30px_-7px_rgba(217,70,168,0.8)]">
                  <div className="absolute inset-[1px] rounded-2xl bg-black/10" />
                  <ArrowDownToLine
                    size={21}
                    strokeWidth={2.3}
                    className="relative text-white"
                  />
                </div>

                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-bold tracking-tight text-white sm:text-lg">
                      Withdraw USDT
                    </h2>
                    <span className="hidden rounded-full border border-[#3FE0B4]/20 bg-[#3FE0B4]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#3FE0B4] sm:inline-flex">
                      Secure
                    </span>
                  </div>

                  <p className="max-w-md text-xs leading-relaxed text-[#C9A8CE]">
                    Submit your withdrawal request securely for admin review.
                  </p>
                </div>
              </div>

              <div className="hidden rounded-xl border border-white/10 bg-white/[0.04] p-2.5 sm:block">
                <ShieldCheck size={18} className="text-[#3FE0B4]" />
              </div>
            </div>

            {/* Balance */}
            <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/25 p-4 backdrop-blur-xl">
                <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-[#D946A8]/10 blur-3xl" />

                <div className="relative">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#B79CC4]">
                    <Wallet size={13} />
                    Available Balance
                  </div>

                  <div className="mt-1 flex items-end gap-2">
                    <span className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                      {balance.toFixed(2)}
                    </span>
                    <span className="mb-1 text-sm font-semibold text-[#B79CC4]">
                      USDT
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center rounded-2xl border border-[#D946A8]/15 bg-[#D946A8]/5 px-5 py-3">
                <div className="text-center">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-[#8B92B8]">
                    Withdrawal
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-[#F0A8DD]">
                    <Send size={12} />
                    USDT
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {message ? (
          <div className="animate-[fadeIn_0.25s_ease-out] flex items-start gap-3 rounded-2xl border border-[#3FE0B4]/25 bg-gradient-to-r from-[#3FE0B4]/10 to-transparent px-4 py-3.5 text-sm text-[#3FE0B4] shadow-[0_10px_35px_-25px_rgba(63,224,180,0.7)]">
            <div className="mt-0.5 rounded-full bg-[#3FE0B4]/10 p-1">
              <CheckCircle2 size={16} />
            </div>
            <div className="leading-6">{message}</div>
          </div>
        ) : null}

        {error ? (
          <div className="animate-[fadeIn_0.25s_ease-out] flex items-start gap-3 rounded-2xl border border-[#E5566B]/25 bg-gradient-to-r from-[#E5566B]/10 to-transparent px-4 py-3.5 text-sm text-[#F07889] shadow-[0_10px_35px_-25px_rgba(229,86,107,0.7)]">
            <div className="mt-0.5 rounded-full bg-[#E5566B]/10 p-1">
              <AlertCircle size={16} />
            </div>
            <div className="leading-6">{error}</div>
          </div>
        ) : null}

        {/* Main form */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0E101A]/75 p-5 shadow-[0_20px_70px_-45px_rgba(0,0,0,0.9)] backdrop-blur-2xl sm:p-6">
          <div className="pointer-events-none absolute right-0 top-0 h-48 w-48 rounded-full bg-[#D946A8]/5 blur-[80px]" />

          <div className="relative mb-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#D946A8]/20 bg-[#D946A8]/10">
                <Sparkles size={16} className="text-[#D946A8]" />
              </div>

              <div>
                <h2 className="text-sm font-bold text-white sm:text-base">
                  Withdrawal Details
                </h2>
                <p className="mt-0.5 text-[11px] text-[#686F91]">
                  Enter your withdrawal information below.
                </p>
              </div>
            </div>

            <div className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-[#747B9D] sm:flex">
              <ShieldCheck size={11} />
              Protected
            </div>
          </div>

          <form onSubmit={handleSubmit} className="relative space-y-5">
            {/* Amount */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-semibold text-[#9A9FBD]">
                  Amount
                </label>

                <button
                  type="button"
                  onClick={function () {
                    setAmount(balance > 0 ? balance.toFixed(2) : "");
                  }}
                  className="text-[10px] font-semibold text-[#D946A8] transition-colors hover:text-[#F0A8DD]"
                >
                  MAX
                </button>
              </div>

              <div className="group relative">
                <div className="pointer-events-none absolute left-4 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2">
                  <Wallet
                    size={16}
                    className="text-[#D946A8] transition-transform duration-200 group-focus-within:scale-110"
                  />
                </div>

                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={balance}
                  value={amount}
                  onChange={function (e) {
                    setAmount(e.target.value);
                  }}
                  placeholder="0.00"
                  required
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.035] py-3.5 pl-11 pr-20 text-base font-semibold text-white outline-none transition-all duration-200 placeholder:text-[#4F5678] hover:border-white/15 hover:bg-white/[0.05] focus:border-[#D946A8]/70 focus:bg-white/[0.055] focus:shadow-[0_0_0_3px_rgba(217,70,168,0.10)]"
                />

                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-[#777E9E]">
                  USDT
                </span>
              </div>

              <div className="mt-2 flex items-center justify-between text-[10px] text-[#666D8D]">
                <span>Minimum: 0.01 USDT</span>
                <span>Available: {balance.toFixed(2)} USDT</span>
              </div>
            </div>

            {/* Wallet address */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <label className="text-xs font-semibold text-[#9A9FBD]">
                  Wallet Address
                </label>
                <span className="rounded-full bg-white/[0.04] px-2 py-0.5 text-[9px] text-[#656C8C]">
                  Required
                </span>
              </div>

              <div className="group relative">
                <input
                  type="text"
                  value={walletAddress}
                  onChange={function (e) {
                    setWalletAddress(e.target.value);
                  }}
                  placeholder="Enter your USDT wallet address"
                  required
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3.5 text-sm text-white outline-none transition-all duration-200 placeholder:text-[#4F5678] hover:border-white/15 hover:bg-white/[0.05] focus:border-[#D946A8]/70 focus:bg-white/[0.055] focus:shadow-[0_0_0_3px_rgba(217,70,168,0.10)]"
                />
              </div>

              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#626A8B]">
                <ShieldCheck size={12} className="text-[#3FE0B4]" />
                Make sure the address matches the selected network.
              </div>
            </div>

            {/* Network */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Network size={14} className="text-[#D946A8]" />
                <label className="text-xs font-semibold text-[#9A9FBD]">
                  Select Network
                </label>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                {networks.map(function (n) {
                  const active = network === n.value;

                  return (
                    <button
                      key={n.value}
                      type="button"
                      onClick={function () {
                        setNetwork(n.value);
                      }}
                      className={
                        "group relative overflow-hidden rounded-2xl border px-3 py-3.5 text-left transition-all duration-200 " +
                        (active
                          ? "border-[#D946A8]/70 bg-gradient-to-br from-[#D946A8]/20 via-[#8B5CF6]/10 to-transparent text-white shadow-[0_0_24px_-8px_rgba(217,70,168,0.7)]"
                          : "border-white/10 bg-white/[0.025] text-[#8B92B8] hover:border-white/20 hover:bg-white/[0.05] hover:text-white")
                      }
                    >
                      {active ? (
                        <div className="absolute right-2 top-2">
                          <div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#D946A8]">
                            <CheckCircle2
                              size={11}
                              strokeWidth={3}
                              className="text-white"
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="flex items-center gap-2.5">
                        <div
                          className={
                            "flex h-8 w-8 items-center justify-center rounded-xl text-[10px] font-black " +
                            (active
                              ? "bg-[#D946A8]/15 text-[#F0A8DD]"
                              : "bg-white/[0.05] text-[#666D8D]")
                          }
                        >
                          {n.value === "TRC20"
                            ? "T"
                            : n.value === "ERC20"
                              ? "E"
                              : n.value === "BEP20"
                                ? "B"
                                : "P"}
                        </div>

                        <div>
                          <div className="text-[11px] font-bold">
                            {n.label}
                          </div>
                          <div className="mt-0.5 text-[9px] text-[#626A8B]">
                            {n.value} Network
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Exchange */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-semibold text-[#9A9FBD]">
                  Exchange / Wallet Name
                </label>

                <span className="text-[9px] font-medium uppercase tracking-wider text-[#555C7C]">
                  Optional
                </span>
              </div>

              <input
                type="text"
                value={exchangeName}
                onChange={function (e) {
                  setExchangeName(e.target.value);
                }}
                placeholder="e.g. Binance, Bitget, Trust Wallet"
                className="w-full rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3.5 text-sm text-white outline-none transition-all duration-200 placeholder:text-[#4F5678] hover:border-white/15 hover:bg-white/[0.05] focus:border-[#D946A8]/70 focus:bg-white/[0.055] focus:shadow-[0_0_0_3px_rgba(217,70,168,0.10)]"
              />
            </div>

            {/* Submit */}
            <div className="pt-1">
              <button
                type="submit"
                disabled={loading}
                className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-[#D946A8] via-[#B355C9] to-[#F5C563] px-4 py-4 text-sm font-bold text-white shadow-[0_12px_35px_-15px_rgba(217,70,168,0.8)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-15px_rgba(217,70,168,0.85)] active:translate-y-0 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 group-hover:translate-x-full" />

                {loading ? (
                  <React.Fragment>
                    <Loader2 size={17} className="relative animate-spin" />
                    <span className="relative">Submitting...</span>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <ArrowDownToLine
                      size={17}
                      strokeWidth={2.5}
                      className="relative"
                    />
                    <span className="relative">
                      Submit Withdrawal Request
                    </span>
                    <ChevronRight
                      size={16}
                      className="relative opacity-60 transition-transform duration-200 group-hover:translate-x-0.5"
                    />
                  </React.Fragment>
                )}
              </button>

              <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-[#555C7C]">
                <ShieldCheck size={11} className="text-[#3FE0B4]" />
                Your withdrawal request will be reviewed securely by admin.
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}